import React, { useState, useEffect, useRef } from 'react';
import { Button, Typography, Card, CardContent, Alert, CircularProgress, Box } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { authServices } from '../../services/authentication/authServices';
import '../../css/Password.css';

const RESEND_COOLDOWN = 120;
const OTP_LENGTH = 6;

const VerifyOtp = () => {
    const [digits, setDigits] = useState(Array(OTP_LENGTH).fill(''));
    const [loading, setLoading] = useState(false);
    const [resendLoading, setResendLoading] = useState(false);
    const [error, setError] = useState(null);
    const [timer, setTimer] = useState(RESEND_COOLDOWN);
    const [canResend, setCanResend] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState(null);
    const timerRef = useRef(null);
    const inputRefs = useRef([]);
    const navigate = useNavigate();
    const location = useLocation();
    const email = location.state?.email;
    const initialMessage = location.state?.initialMessage;

    const [successMessage, setSuccessMessage] = useState(initialMessage || null);

    useEffect(() => {
        if (successMessage) {
            const clearTimer = setTimeout(() => setSuccessMessage(null), 4000);
            return () => clearTimeout(clearTimer);
        }
    }, [successMessage]);

    // Redirect if no email in state (direct URL access)
    useEffect(() => {
        if (!email) {
            navigate('/forgot-password');
        }
    }, [email, navigate]);

    // Focus first box on mount
    useEffect(() => {
        inputRefs.current[0]?.focus();
    }, []);

    const startCountdown = () => {
        clearInterval(timerRef.current);
        const storageKey = `otp_timer_${email}`;
        let endTimeStr = sessionStorage.getItem(storageKey);

        let endTime;
        if (endTimeStr) {
            endTime = parseInt(endTimeStr, 10);
            if (endTime <= Date.now()) {
                setCanResend(true);
                setTimer(0);
                sessionStorage.removeItem(storageKey);
                return;
            }
        } else {
            endTime = Date.now() + RESEND_COOLDOWN * 1000;
            sessionStorage.setItem(storageKey, endTime);
        }

        const updateTimer = () => {
            const remaining = Math.ceil((endTime - Date.now()) / 1000);
            if (remaining <= 0) {
                clearInterval(timerRef.current);
                setCanResend(true);
                setTimer(0);
                sessionStorage.removeItem(storageKey);
            } else {
                setCanResend(false);
                setTimer(remaining);
            }
        };

        updateTimer();
        timerRef.current = setInterval(updateTimer, 1000);
    };

    // Countdown timer
    useEffect(() => {
        if (email) {
            startCountdown();
        }
        return () => clearInterval(timerRef.current);
    }, [email]);

    // Backend error constants (must match Constants.java exactly)
    const REDIRECT_ERRORS = [
        'Invalid OTP request',           // INVALID_OTP_REQUEST
        'OTP has already been used',     // OTP_ALREADY_USED
        'Maximum attempts exceeded. Please request a new OTP.', // MAX_ATTEMPTS_EXCEEDED
    ];

    const verifyOtp = async (otpValue) => {
        setLoading(true);
        setError(null);

        try {
            await authServices.verifyOtp(email, otpValue);
            navigate('/reset-password', { state: { email }, replace: true });
        } catch (err) {
            const msg = err.response?.data || 'Invalid or expired OTP. Please try again.';

            if (REDIRECT_ERRORS.includes(msg)) {
                navigate('/forgot-password', {
                    replace: true,
                    state: { errorMessage: msg }
                });
            } else {
                setError(msg);
                setTimeout(() => setError(null), 4000);
                setDigits(Array(OTP_LENGTH).fill(''));
                inputRefs.current[0]?.focus();

                if (msg === 'OTP has expired') {
                    clearInterval(timerRef.current);
                    setCanResend(true);
                    setTimer(0);
                    sessionStorage.removeItem(`otp_timer_${email}`);
                }
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDigitChange = (index, value) => {
        // Allow only a single digit
        const digit = value.replace(/\D/g, '').slice(-1);

        const newDigits = [...digits];
        newDigits[index] = digit;
        setDigits(newDigits);

        // Auto-advance to next box
        if (digit && index < OTP_LENGTH - 1) {
            inputRefs.current[index + 1]?.focus();
        }

        // Auto-verify when all 6 digits filled
        if (digit && index === OTP_LENGTH - 1) {
            const fullOtp = newDigits.join('');
            if (fullOtp.length === OTP_LENGTH) {
                verifyOtp(fullOtp);
            }
        }
    };

    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace') {
            if (digits[index]) {
                // Clear current box
                const newDigits = [...digits];
                newDigits[index] = '';
                setDigits(newDigits);
            } else if (index > 0) {
                // Move to previous box
                inputRefs.current[index - 1]?.focus();
            }
        } else if (e.key === 'ArrowLeft' && index > 0) {
            inputRefs.current[index - 1]?.focus();
        } else if (e.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    // Handle paste — fill all boxes at once
    const handlePaste = (e) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
        if (!pasted) return;

        const newDigits = Array(OTP_LENGTH).fill('');
        pasted.split('').forEach((ch, i) => { newDigits[i] = ch; });
        setDigits(newDigits);

        const nextEmpty = pasted.length < OTP_LENGTH ? pasted.length : OTP_LENGTH - 1;
        inputRefs.current[nextEmpty]?.focus();

        if (pasted.length === OTP_LENGTH) {
            verifyOtp(pasted);
        }
    };

    const handleResend = async () => {
        if (!canResend) return;
        setResendLoading(true);
        setError(null);
        setDigits(Array(OTP_LENGTH).fill(''));
        inputRefs.current[0]?.focus();

        try {
            await authServices.requestOtp(email);
            setSuccessMessage('A new OTP has been sent to your email.');

            // Restart countdown
            sessionStorage.setItem(`otp_timer_${email}`, Date.now() + RESEND_COOLDOWN * 1000);
            startCountdown();
        } catch (err) {
            setError('Failed to resend OTP. Please try again.');
            setTimeout(() => setError(null), 4000);
        } finally {
            setResendLoading(false);
        }
    };

    const getBoxClassName = (digit, index) => {
        let cls = 'otp-box';
        if (digit) cls += ' otp-box--filled';
        if (focusedIndex === index) cls += ' otp-box--focused';
        return cls;
    };

    return (
        <div className="auth-page-wrapper">
            <Card sx={{ width: 420, padding: '1.25rem', borderRadius: '1rem', boxShadow: 3, backgroundColor: '#ffffff' }}>
                <CardContent>
                    <Typography variant="h3" align="center" gutterBottom sx={{
                        fontWeight: 700, fontSize: '2rem', color: '#045864', letterSpacing: '2px'
                    }}>
                        Verify OTP
                    </Typography>

                    <Typography variant="body1" align="center" sx={{ color: '#64748b', mb: 2, fontSize: '0.9rem' }}>
                        We've sent a 6-digit OTP to <strong>{email}</strong>.<br />Enter it below.
                    </Typography>

                    {successMessage && (
                        <Alert variant="filled" severity="success" sx={{ mb: 2 }}>
                            {successMessage}
                        </Alert>
                    )}
                    {error && (
                        <Alert variant="outlined" severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {/* 6-Box OTP Input */}
                    <div className="otp-boxes-row">
                        {digits.map((digit, index) => (
                            <input
                                key={index}
                                ref={el => inputRefs.current[index] = el}
                                type="text"
                                inputMode="numeric"
                                maxLength={1}
                                value={digit}
                                onChange={e => handleDigitChange(index, e.target.value)}
                                onKeyDown={e => handleKeyDown(index, e)}
                                onPaste={index === 0 ? handlePaste : undefined}
                                onFocus={e => { setFocusedIndex(index); e.target.select(); }}
                                onBlur={() => setFocusedIndex(null)}
                                className={getBoxClassName(digit, index)}
                            />
                        ))}
                    </div>

                    {/* Loading indicator while verifying */}
                    {loading && (
                        <div className="otp-verifying-row">
                            <CircularProgress size={18} sx={{ color: '#045864' }} />
                            <Typography variant="body2" sx={{ color: '#045864', fontSize: '0.875rem' }}>
                                Verifying...
                            </Typography>
                        </div>
                    )}

                    {/* Resend OTP */}
                    <Box sx={{ textAlign: 'center', mt: 1 }}>
                        {canResend ? (
                            <Button
                                variant="text"
                                onClick={handleResend}
                                disabled={resendLoading}
                                sx={{ color: '#045864', textTransform: 'none', fontSize: '0.875rem' }}
                            >
                                {resendLoading ? <CircularProgress size={16} /> : 'Resend OTP'}
                            </Button>
                        ) : (
                            <Typography variant="body2" sx={{ color: '#94a3b8', fontSize: '0.85rem' }}>
                                Resend OTP in <strong style={{ color: '#045864' }}>
                                    {Math.floor(timer / 60).toString().padStart(2, '0')}:{(timer % 60).toString().padStart(2, '0')}
                                </strong>
                            </Typography>
                        )}
                    </Box>

                    <div className="auth-back-link">
                        <Button
                            variant="text"
                            onClick={() => navigate('/forgot-password', { replace: true })}
                            sx={{ color: '#64748b', textTransform: 'none', fontSize: '0.8rem' }}
                        >
                            ← Change Email
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default VerifyOtp;
